/**
 * Client-side mirror of the backend password / PIN policy
 * (`rb-api/src/shared/security/password-policy.ts`).
 *
 * The backend is always the authority; this exists so the user gets immediate,
 * specific feedback in the form instead of a round-trip rejection. Keep the two
 * files in sync when the policy changes.
 *
 * Policy (ENS RD 311/2022 / AEPD / NIST SP 800-63B):
 *  - 12–128 characters, internal spaces allowed (passphrases).
 *  - No mandatory composition rules, no forced rotation.
 *  - Reject common secrets, username-derived secrets, and obvious
 *    runs / repetitions.
 */

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const PIN_REGEX = /^\d{4}$/;

const COMMON_PASSWORDS = new Set(
  [
    'password',
    'passw0rd',
    'password1',
    'password12',
    'password123',
    'password1234',
    'contraseña',
    'contrasena',
    'contraseña1',
    'contrasena1',
    'qwerty',
    'qwertyuiop',
    'qwerty123',
    'qwerty12345',
    '1q2w3e4r',
    '1q2w3e4r5t',
    'admin',
    'administrator',
    'administrador',
    'welcome',
    'welcome1',
    'bienvenido',
    'iloveyou',
    'letmein',
    'dragon',
    'monkey',
    'football',
    'superman',
    'trustno1',
    'clinica',
    'clinicadental',
    'dental',
    'ruizbusquets',
    '123456',
    '1234567',
    '12345678',
    '123456789',
    '1234567890',
    '12345678910',
    '111111',
    '000000',
    'abc123',
    'abcd1234',
    'aaaaaaaaaaaa',
    'changeme',
    'cambiame',
  ].map((value) => value.toLowerCase()),
);

const TRIVIAL_PINS = new Set([
  '0000',
  '1111',
  '2222',
  '3333',
  '4444',
  '5555',
  '6666',
  '7777',
  '8888',
  '9999',
  '1234',
  '2345',
  '3456',
  '4567',
  '5678',
  '6789',
  '0123',
  '9876',
  '8765',
  '7654',
  '6543',
  '5432',
  '4321',
  '3210',
  '1212',
  '2121',
  '1122',
  '6969',
  '2580',
]);

function hasLongRun(value: string): boolean {
  if (/(.)\1{3,}/.test(value)) {
    return true;
  }

  const lower = value.toLowerCase();
  for (let index = 0; index + 4 < lower.length; index += 1) {
    let ascending = true;
    let descending = true;
    for (let offset = 1; offset < 5; offset += 1) {
      const delta =
        lower.charCodeAt(index + offset) - lower.charCodeAt(index + offset - 1);
      if (delta !== 1) {
        ascending = false;
      }
      if (delta !== -1) {
        descending = false;
      }
    }
    if (ascending || descending) {
      return true;
    }
  }

  return false;
}

export interface PasswordAssessment {
  valid: boolean;
  message?: string;
}

export function assessPasswordStrength(
  rawPassword: unknown,
  username?: string,
): PasswordAssessment {
  if (typeof rawPassword !== 'string') {
    return { valid: false, message: 'La contraseña no es válida' };
  }

  const password = rawPassword.trim();

  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      valid: false,
      message: `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`,
    };
  }

  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      valid: false,
      message: `La contraseña no puede superar los ${PASSWORD_MAX_LENGTH} caracteres`,
    };
  }

  const lower = password.toLowerCase();

  if (COMMON_PASSWORDS.has(lower)) {
    return {
      valid: false,
      message: 'La contraseña es demasiado común. Elige otra distinta',
    };
  }

  if (new Set(password).size < 5) {
    return {
      valid: false,
      message: 'La contraseña tiene muy poca variedad de caracteres',
    };
  }

  if (hasLongRun(password)) {
    return {
      valid: false,
      message:
        'La contraseña no puede contener secuencias o repeticiones evidentes',
    };
  }

  const normalizedUsername = username?.trim().toLowerCase();
  if (normalizedUsername && normalizedUsername.length >= 3) {
    if (lower.includes(normalizedUsername) || normalizedUsername.includes(lower)) {
      return {
        valid: false,
        message: 'La contraseña no puede basarse en el nombre de usuario',
      };
    }
  }

  return { valid: true };
}

export interface PinAssessment {
  valid: boolean;
  message?: string;
}

export function assessPin(rawPin: unknown): PinAssessment {
  const pin = String(rawPin ?? '').trim();

  if (!PIN_REGEX.test(pin)) {
    return { valid: false, message: 'El PIN debe tener 4 dígitos' };
  }

  if (TRIVIAL_PINS.has(pin)) {
    return {
      valid: false,
      message:
        'El PIN es demasiado sencillo. Elige una combinación menos obvia',
    };
  }

  return { valid: true };
}

/**
 * Coarse 0–4 strength score for the meter under the password field. It is a
 * UX hint only – it never gates submission (`assessPasswordStrength` does).
 */
export function scorePassword(rawPassword: string): number {
  const password = (rawPassword || '').trim();
  if (!password) {
    return 0;
  }

  let score = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) score += 1;
  if (password.length >= 16) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score += 1;
  if (/\s/.test(password) && password.length >= 16) score += 1;

  if (assessPasswordStrength(password).valid === false) {
    score = Math.min(score, 1);
  }

  return Math.min(score, 4);
}

export const PASSWORD_STRENGTH_LABELS = [
  'Muy débil',
  'Débil',
  'Aceptable',
  'Buena',
  'Excelente',
];
